import React from 'react'
import { Box, Typography } from '@mui/material'
import { useAuth } from 'hooks/auth/useAuth'
import { Warning } from '@mui/icons-material'

export const SessionExpiringSoon = () => {
  const { remainingSessionTime, logout, currentUser } = useAuth()
  const [timeLeft, setTimeLeft] = React.useState<number | null>(null)
  const hasLoggedOutRef = React.useRef(false)

  const oneHourInMilliseconds = 60 * 60 * 1000

  // Sync from auth context; clear local countdown when session is cleared
  // (logout / multi-tab sign-out) so a stale timer cannot force-logout
  // a freshly authenticated session.
  React.useEffect(() => {
    hasLoggedOutRef.current = false
    setTimeLeft(remainingSessionTime)
  }, [remainingSessionTime])

  React.useEffect(() => {
    if (!currentUser || remainingSessionTime == null) {
      return
    }

    const timer = window.setInterval(() => {
      setTimeLeft(prev => {
        if (prev == null) return null
        if (prev <= 1000) {
          if (!hasLoggedOutRef.current) {
            hasLoggedOutRef.current = true
            void logout().then(() => {
              window.location.href = '/session-expired'
            })
          }
          return 0
        }
        return prev - 1000
      })
    }, 1000)

    return () => window.clearInterval(timer)
  }, [currentUser, remainingSessionTime, logout])

  if (!currentUser || !timeLeft || timeLeft > oneHourInMilliseconds) {
    return null
  }

  const minutes = Math.floor(timeLeft / 60000)
  const seconds = Math.floor((timeLeft % 60000) / 1000)

  return (
    <>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          marginRight: 2,
          '&:hover .warning-text': {
            display: 'block',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Warning
            sx={{
              color: 'warning.main',
              display: 'flex',
              alignItems: 'center',
            }}
          />
          <Typography
            className='warning-text'
            sx={{
              position: 'absolute',
              bottom: '-40px',
              right: 0,
              backgroundColor: 'warning.main',
              color: 'warning.contrastText',
              padding: '8px',
              borderRadius: '4px',
              whiteSpace: 'nowrap',
              display: 'none',
              zIndex: 1000,
            }}
          >
            Warning: Session expires in {minutes}m {seconds}s. You will be logged out.
          </Typography>
        </Box>
      </Box>
    </>
  )
}
